import React, { useEffect, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import { AppsSDKUIProvider } from "@openai/apps-sdk-ui/components/AppsSDKUIProvider";
import { ImportProfileDialog, ProfileDialog, ProfileMenu, readTravellerProfile } from "./demo_profile.tsx";
import { ItineraryWidget } from "./itineraries.tsx";
import { buildResultsState, integrateWidget, itineraryCount, type ResultsState } from "./result_sets.ts";
import { GeminiLiveConnection, type GeminiLiveSessionData } from "./demo_voice_gemini.ts";
import { appPath } from "./app_path.ts";

type VoiceRole = "user" | "assistant";
type VoiceMessage = { id: string; role: VoiceRole; content: string; sequence: number };
type VoiceToolCall = { name: string; arguments: any; response: any };
type VoiceUsage = {
  model?: string;
  transcription_model?: string;
  reasoning_effort?: string;
  input_tokens?: number;
  cached_input_tokens?: number;
  output_tokens?: number;
  total_tokens?: number;
  input_text_tokens?: number;
  input_audio_tokens?: number;
  output_text_tokens?: number;
  output_audio_tokens?: number;
  transcription_tokens?: number;
  transcription_seconds?: number;
  estimated_cost_usd?: number;
  realtime_estimated_cost_usd?: number;
  transcription_estimated_cost_usd?: number;
};
type VoiceState = { messages?: VoiceMessage[]; widgets?: any[]; tool_calls?: VoiceToolCall[]; voice_usage?: VoiceUsage; can_write?: boolean };
type VoicePhase = "idle" | "connecting" | "listening" | "thinking" | "speaking" | "searching" | "ended" | "error";
type VoiceRecovery = { action?: "start" | "reset"; reloadAttempts?: number; turn?: VoiceMessage };
type VoiceProvider = "openai" | "gemini";

const VOICE_PROVIDER_COOKIE = "reagent_demo_voice_model_selection";

function voiceDemoBasePath() {
  return document.getElementById("demo-voice-root")?.dataset.demoVoiceBasePath || appPath("/demo-voice");
}

function readVoiceProvider(): VoiceProvider {
  try {
    const cookie = document.cookie.split("; ").find((item) => item.startsWith(`${VOICE_PROVIDER_COOKIE}=`));
    if (!cookie) return "openai";
    const value = JSON.parse(decodeURIComponent(cookie.slice(cookie.indexOf("=") + 1)));
    return value?.v === 1 && value?.provider === "gemini" ? "gemini" : "openai";
  } catch {
    return "openai";
  }
}

function storeVoiceProvider(provider: VoiceProvider) {
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${VOICE_PROVIDER_COOKIE}=${encodeURIComponent(JSON.stringify({ v: 1, provider }))}; Path=/; Max-Age=31536000; SameSite=Lax${secure}`;
}

class StaleVoiceClientError extends Error {}

const PHASE_LABELS: Record<VoicePhase, string> = {
  idle: "Ready when you are",
  connecting: "Connecting...",
  listening: "Listening",
  thinking: "Thinking",
  speaking: "Reagent is speaking",
  searching: "Searching flights",
  ended: "Conversation ended",
  error: "Connection interrupted",
};

function currentChatId() {
  const match = window.location.pathname.match(new RegExp(`^${voiceDemoBasePath()}/([^/?#]+)`));
  return match ? decodeURIComponent(match[1]) : null;
}

function csrfToken() {
  return document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || "";
}

function voiceRecoveryStorageKey() {
  return `demo-voice-recovery:${window.location.pathname}`;
}

function parseVoiceRecovery(value: any): VoiceRecovery | undefined {
  if (!value || typeof value !== "object") return;
  if (value.action && !["start", "reset"].includes(value.action)) return;
  if (value.reloadAttempts && (!Number.isInteger(value.reloadAttempts) || value.reloadAttempts < 0)) return;
  if (value.turn && (typeof value.turn.id !== "string" || !["user", "assistant"].includes(value.turn.role) ||
    typeof value.turn.content !== "string" || !Number.isInteger(value.turn.sequence))) return;
  return value;
}

function takeVoiceRecovery(): VoiceRecovery | undefined {
  const key = voiceRecoveryStorageKey();

  try {
    const rawRecovery = sessionStorage.getItem(key);
    sessionStorage.removeItem(key);
    if (rawRecovery) return parseVoiceRecovery(JSON.parse(rawRecovery));
  } catch {
    // Fall back to history state when session storage is unavailable.
  }

  try {
    const state = window.history.state;
    const recovery = parseVoiceRecovery(state?.demoVoiceRecovery);
    if (state?.demoVoiceRecovery) {
      const { demoVoiceRecovery: _, ...rest } = state;
      window.history.replaceState(rest, "", window.location.href);
    }
    return recovery;
  } catch {
    return;
  }
}

async function jsonRequest(url: string, options: RequestInit = {}, demoRelease?: string) {
  const response = await fetch(url, {
    ...options,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "X-CSRF-Token": csrfToken(),
      ...(demoRelease ? { "X-Demo-Release": demoRelease } : {}),
      ...options.headers,
    },
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    if (response.status === 409 && data?.error?.code === "STALE_CLIENT") throw new StaleVoiceClientError();
    const error = new Error(data?.error?.message || "The request failed.");
    (error as any).data = data;
    throw error;
  }
  return data;
}

function upsertMessage(messages: VoiceMessage[], message: VoiceMessage) {
  const existing = messages.findIndex((item) => item.id === message.id);
  if (existing === -1) return [...messages, message].sort((a, b) => a.sequence - b.sequence);
  const next = [...messages];
  next[existing] = message;
  return next.sort((a, b) => a.sequence - b.sequence);
}

function FlightResults({ resultsState, onSelect }: { resultsState: ResultsState; onSelect: (resultSetId: string) => void }) {
  const theme = document.documentElement.classList.contains("dark") ? "dark" : "light";
  const activeResultSet = resultsState.sets.find((resultSet) => resultSet.id === resultsState.selectedId) || resultsState.sets.at(-1);
  if (!activeResultSet) return null;

  return (
    <section aria-label="Flight results" className="mx-auto w-full max-w-[1100px] px-0 pb-32 sm:px-5">
      {resultsState.sets.length > 1 ? (
        <div role="tablist" aria-label="Flight searches" className="mb-3 hidden flex-wrap gap-2 lg:flex">
          {resultsState.sets.map((resultSet) => {
            const selected = resultSet.id === activeResultSet.id;
            const count = itineraryCount(resultSet.widget);
            return (
              <button
                key={resultSet.id}
                type="button"
                role="tab"
                aria-selected={selected}
                aria-controls="voice-results-content"
                onClick={() => onSelect(resultSet.id)}
                className={`max-w-full rounded-full border px-3 py-1.5 text-left text-xs font-medium transition focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${selected ? "border-blue-600 bg-blue-600 text-white dark:border-blue-400 dark:bg-blue-400 dark:text-gray-950" : "border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:bg-gray-50 dark:border-white/10 dark:bg-white/5 dark:text-gray-300 dark:hover:bg-white/10"}`}
              >
                <span className="block max-w-64 truncate">{resultSet.label}</span>
                <span className={selected ? "text-blue-100 dark:text-gray-800" : "text-gray-400"}>{count} {count === 1 ? "itinerary" : "itineraries"}</span>
              </button>
            );
          })}
        </div>
      ) : null}

      {resultsState.sets.length > 1 ? (
        <div className="mb-3 grid grid-cols-2 gap-2 px-3 lg:hidden">
          {resultsState.sets.map((resultSet, index) => {
            const selected = resultSet.id === activeResultSet.id;
            const count = itineraryCount(resultSet.widget);
            const spansFullWidth = resultsState.sets.length % 2 === 1 && index === resultsState.sets.length - 1;
            return (
              <button
                key={resultSet.id}
                type="button"
                onClick={() => onSelect(resultSet.id)}
                className={`${spansFullWidth ? "col-span-2" : "col-span-1"} min-w-0 rounded-2xl border px-3 py-2.5 text-left transition focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${selected ? "border-blue-200 bg-blue-50 text-blue-800 dark:border-blue-400/30 dark:bg-blue-400/10 dark:text-blue-200" : "border-gray-200 bg-white text-gray-700 dark:border-white/10 dark:bg-white/5 dark:text-gray-200"}`}
              >
                <span className="block truncate text-[11px] font-medium text-gray-500 dark:text-gray-400">{resultSet.label}</span>
                <span className="block truncate text-sm font-semibold">{selected ? "Viewing" : "View"} {count === 1 ? "itinerary" : `all ${count} itineraries`}</span>
              </button>
            );
          })}
        </div>
      ) : null}

      <div id="voice-results-content" role="tabpanel" className="demo-voice-itinerary-widget overflow-hidden border-y border-gray-200 bg-white py-2 dark:border-white/10 dark:bg-black sm:rounded-2xl sm:border" data-demo-theme={theme}>
        <AppsSDKUIProvider>
          <ItineraryWidget key={activeResultSet.id} toolOutputOverride={activeResultSet.widget} themeOverride={theme} />
        </AppsSDKUIProvider>
      </div>
    </section>
  );
}

function formatTokenCount(value?: number) {
  return Number(value || 0).toLocaleString();
}

function formatEstimatedCost(value?: number) {
  const cost = Number(value || 0);
  return `$${cost < 0.01 ? cost.toFixed(4) : cost.toFixed(2)}`;
}

function VoiceUsageMetric({ label, value, wide = false }: { label: string; value: string; wide?: boolean }) {
  return (
    <div className={wide ? "col-span-2 min-w-0 sm:col-span-1" : "min-w-0"}>
      <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">{label}</div>
      <div className="truncate font-mono text-xs text-gray-700 dark:text-gray-200">{value}</div>
    </div>
  );
}

function VoiceUsageSummary({ usage }: { usage: VoiceUsage }) {
  return (
    <div className="grid grid-cols-2 gap-x-5 gap-y-3 rounded-xl border border-gray-200 bg-white p-3 sm:grid-cols-4 dark:border-white/10 dark:bg-black/30">
      <VoiceUsageMetric label="model" value={usage.model || "gpt-realtime-2.1"} wide />
      <VoiceUsageMetric label="effort" value={usage.reasoning_effort || "medium"} />
      <VoiceUsageMetric label="estimated cost" value={formatEstimatedCost(usage.estimated_cost_usd)} />
      <VoiceUsageMetric label="total tokens" value={formatTokenCount(usage.total_tokens)} />
      <VoiceUsageMetric label="input" value={formatTokenCount(usage.input_tokens)} />
      <VoiceUsageMetric label="cached" value={formatTokenCount(usage.cached_input_tokens)} />
      <VoiceUsageMetric label="output" value={formatTokenCount(usage.output_tokens)} />
      <VoiceUsageMetric label="audio input" value={formatTokenCount(usage.input_audio_tokens)} />
      <VoiceUsageMetric label="audio output" value={formatTokenCount(usage.output_audio_tokens)} />
      <VoiceUsageMetric label="text input" value={formatTokenCount(usage.input_text_tokens)} />
      <VoiceUsageMetric label="text output" value={formatTokenCount(usage.output_text_tokens)} />
      <VoiceUsageMetric label="realtime cost" value={formatEstimatedCost(usage.realtime_estimated_cost_usd)} />
      <VoiceUsageMetric label="transcription tokens" value={formatTokenCount(usage.transcription_tokens)} />
      <VoiceUsageMetric label="transcription seconds" value={Number(usage.transcription_seconds || 0).toFixed(1)} />
      <VoiceUsageMetric label="transcription cost" value={formatEstimatedCost(usage.transcription_estimated_cost_usd)} />
    </div>
  );
}

function ToolCallsDebug({ toolCalls, usage }: { toolCalls: VoiceToolCall[]; usage?: VoiceUsage }) {
  if (!toolCalls.length && !usage?.total_tokens && !usage?.transcription_seconds) return null;

  return (
    <details className="mx-auto mb-32 w-[calc(100%-2rem)] max-w-[1060px] rounded-xl border border-gray-200 bg-white/70 text-left dark:border-white/10 dark:bg-white/5">
      <summary className="cursor-pointer px-4 py-3 text-xs font-semibold uppercase tracking-[0.14em] text-gray-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-500 dark:text-gray-400">
        Debug: usage · {toolCalls.length} tool {toolCalls.length === 1 ? "call" : "calls"}
      </summary>
      {usage ? <div className="border-t border-gray-200 p-3 dark:border-white/10"><VoiceUsageSummary usage={usage} /></div> : null}
      <ol className="divide-y divide-gray-200 border-t border-gray-200 dark:divide-white/10 dark:border-white/10">
        {toolCalls.map((toolCall, index) => (
          <li key={`${toolCall.name}-${index}`} className="px-4 py-4">
            <div className="mb-3 flex items-center gap-2">
              <span className="flex h-5 w-5 items-center justify-center rounded-full bg-blue-100 text-[11px] font-bold text-blue-700 dark:bg-blue-500/20 dark:text-blue-300">{index + 1}</span>
              <code className="text-sm font-semibold text-gray-800 dark:text-gray-200">{toolCall.name}</code>
            </div>
            <div className="grid gap-3 lg:grid-cols-2">
              <div>
                <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400">Arguments</div>
                <pre className="max-h-64 overflow-auto rounded-lg bg-gray-950 p-3 text-xs leading-5 text-gray-100">{JSON.stringify(toolCall.arguments, null, 2)}</pre>
              </div>
              <div>
                <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400">Response</div>
                <pre className="max-h-64 overflow-auto rounded-lg bg-gray-950 p-3 text-xs leading-5 text-gray-100">{JSON.stringify(toolCall.response, null, 2)}</pre>
              </div>
            </div>
          </li>
        ))}
      </ol>
    </details>
  );
}

function TranscriptDrawer({ messages, open, onClose }: { messages: VoiceMessage[]; open: boolean; onClose: () => void }) {
  const closeRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (!open) return;

    closeRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
      if (event.key === "Tab") {
        event.preventDefault();
        closeRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-40" role="dialog" aria-modal="true" aria-labelledby="voice-transcript-title">
      <button type="button" aria-label="Close transcript" className="absolute inset-0 bg-black/35 backdrop-blur-[2px]" onClick={onClose} />
      <aside className="absolute inset-y-0 right-0 flex w-full max-w-md flex-col border-l border-gray-200 bg-white shadow-2xl dark:border-white/10 dark:bg-[#181818]">
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4 dark:border-white/10">
          <div>
            <h2 id="voice-transcript-title" className="font-semibold">Transcript</h2>
          </div>
          <button ref={closeRef} type="button" onClick={onClose} aria-label="Close transcript" className="flex h-10 w-10 items-center justify-center rounded-full hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 dark:hover:bg-white/10">
            <span aria-hidden="true" className="text-xl">x</span>
          </button>
        </div>
        <div className="flex-1 space-y-5 overflow-y-auto px-5 py-6">
          {messages.length ? messages.map((message) => (
            <div key={message.id}>
              <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-400">{message.role === "user" ? "You" : "Reagent"}</div>
              <p className="whitespace-pre-wrap text-[15px] leading-6 text-gray-800 dark:text-gray-200">{message.content}</p>
            </div>
          )) : <p className="text-sm text-gray-500">Your conversation will appear here after you begin.</p>}
        </div>
      </aside>
    </div>
  );
}

async function retryOnce<T>(operation: () => Promise<T>) {
  try {
    return await operation();
  } catch {
    return operation();
  }
}

function VoiceModelSelector({ provider, disabled, onSelect }: {
  provider: VoiceProvider;
  disabled: boolean;
  onSelect: (provider: VoiceProvider) => void;
}) {
  const detailsRef = useRef<HTMLDetailsElement | null>(null);

  useEffect(() => {
    const close = (event: Event) => {
      if (detailsRef.current && !detailsRef.current.contains(event.target as Node)) detailsRef.current.open = false;
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape" && detailsRef.current?.open) {
        detailsRef.current.open = false;
        detailsRef.current.querySelector("summary")?.focus();
      }
    };
    document.addEventListener("pointerdown", close);
    window.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", close);
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, []);

  return (
    <details ref={detailsRef} className="relative" onClick={(event) => { if (disabled) event.preventDefault(); }}>
      <summary aria-label={`Voice model: ${provider === "gemini" ? "Gemini Live" : "OpenAI Realtime"}`} className={`list-none rounded-full px-2 py-2 text-sm font-medium text-gray-600 transition focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 dark:text-gray-300 sm:px-3 ${disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer hover:bg-black/5 dark:hover:bg-white/10"}`}>
        Model
      </summary>
      <div className="fixed left-3 right-3 top-[4.25rem] z-30 overflow-hidden rounded-2xl border border-gray-200 bg-white p-2 text-left shadow-xl dark:border-white/10 dark:bg-[#202020] sm:absolute sm:left-auto sm:right-0 sm:top-full sm:mt-2 sm:w-64">
        {(["openai", "gemini"] as VoiceProvider[]).map((option) => {
          const selected = provider === option;
          return (
            <button
              key={option}
              type="button"
              role="menuitemradio"
              aria-checked={selected}
              onClick={() => {
                onSelect(option);
                if (detailsRef.current) detailsRef.current.open = false;
              }}
              className={`block w-full rounded-xl px-3 py-2.5 text-left transition ${selected ? "bg-blue-50 text-blue-800 dark:bg-blue-500/15 dark:text-blue-200" : "hover:bg-gray-100 dark:hover:bg-white/10"}`}
            >
              <span className="block text-sm font-semibold">{option === "gemini" ? "Gemini Live" : "OpenAI Realtime"}</span>
              <span className="block text-xs text-gray-500 dark:text-gray-400">{option === "gemini" ? "Gemini 3.1 Flash Live" : "GPT Realtime 2.1"}</span>
            </button>
          );
        })}
      </div>
    </details>
  );
}

function IconButton({ label, active = false, danger = false, disabled = false, onClick, children }: {
  label: string;
  active?: boolean;
  danger?: boolean;
  disabled?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  const color = danger
    ? "bg-red-600 text-white hover:bg-red-500"
    : active
      ? "bg-gray-900 text-white dark:bg-white dark:text-gray-900"
      : "border border-gray-200 bg-white text-gray-800 hover:bg-gray-50 dark:border-white/15 dark:bg-[#242424] dark:text-gray-100 dark:hover:bg-[#303030]";

  return (
    <button type="button" onClick={onClick} disabled={disabled} aria-label={label} aria-pressed={label.includes("microphone") ? active : undefined} className={`flex h-12 w-12 cursor-pointer items-center justify-center rounded-full shadow-sm transition focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-40 ${color}`}>
      {children}
    </button>
  );
}

function VoiceOrb({ phase }: { phase: VoicePhase }) {
  return (
    <div className={`voice-orb voice-orb--${phase}`} aria-hidden="true">
      <div className="voice-orb__halo" />
      <div className="voice-orb__body">
        <div className="voice-orb__wash voice-orb__wash--one" />
        <div className="voice-orb__wash voice-orb__wash--two" />
        <div className="voice-orb__shine" />
      </div>
    </div>
  );
}

function VoiceDemoApp({
  debugEnabled = true,
  demoRelease,
  recoveredAction,
  voiceOnly = false,
}: {
  debugEnabled?: boolean;
  demoRelease?: string;
  recoveredAction?: VoiceRecovery;
  voiceOnly?: boolean;
}) {
  const [messages, setMessages] = useState<VoiceMessage[]>([]);
  const [resultsState, setResultsState] = useState<ResultsState>({ sets: [], selectedId: null });
  const [toolCalls, setToolCalls] = useState<VoiceToolCall[]>([]);
  const [voiceUsage, setVoiceUsage] = useState<VoiceUsage | undefined>();
  const [phase, setPhase] = useState<VoicePhase>("idle");
  const [caption, setCaption] = useState("Tell me about your trip");
  const [captionRole, setCaptionRole] = useState<VoiceRole | null>(null);
  const [muted, setMuted] = useState(false);
  const [provider, setProvider] = useState<VoiceProvider>(() => readVoiceProvider());
  const [transcriptOpen, setTranscriptOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [profileImportOpen, setProfileImportOpen] = useState(false);
  const [profileRevision, setProfileRevision] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [stateLoaded, setStateLoaded] = useState(false);
  const [stateReady, setStateReady] = useState(false);
  const [canWrite, setCanWrite] = useState(false);
  const [recoveryPending, setRecoveryPending] = useState(!!recoveredAction?.action);
  const peerRef = useRef<RTCPeerConnection | null>(null);
  const channelRef = useRef<RTCDataChannel | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const geminiRef = useRef<GeminiLiveConnection | null>(null);
  const providerRef = useRef<VoiceProvider>(provider);
  const messagesRef = useRef<VoiceMessage[]>([]);
  const resultsStateRef = useRef<ResultsState>({ sets: [], selectedId: null });
  const processedCallsRef = useRef(new Set<string>());
  const processedUsageRef = useRef(new Set<string>());
  const assistantDraftRef = useRef("");
  const geminiUserDraftRef = useRef("");
  const geminiUserTurnIdRef = useRef<string | null>(null);
  const geminiAssistantTurnIdRef = useRef<string | null>(null);
  const geminiUsageSequenceRef = useRef(0);
  const geminiFinalizeTimerRef = useRef<number | null>(null);
  const cancelledGeminiCallsRef = useRef(new Set<string>());
  const geminiToolAbortRef = useRef(new Map<string, AbortController>());
  const sequenceRef = useRef(0);
  const itemSequencesRef = useRef(new Map<string, number>());
  const connectionGenerationRef = useRef(0);
  const transcriptButtonRef = useRef<HTMLButtonElement | null>(null);
  const profileButtonRef = useRef<HTMLElement | null>(null);
  const recoveredActionRef = useRef(recoveredAction?.action);
  const recoveryAttemptsRef = useRef(recoveredAction?.reloadAttempts || 0);
  const reloadingRef = useRef(false);

  function reloadForStaleClient(action?: VoiceRecovery["action"], turn?: VoiceMessage) {
    if (reloadingRef.current) return;
    if (recoveryAttemptsRef.current >= 2) {
      recoveredActionRef.current = undefined;
      recoveryAttemptsRef.current = 0;
      setRecoveryPending(false);
      setError("The connection could not be refreshed automatically. Please refresh the page and try again.");
      return;
    }

    reloadingRef.current = true;
    setRecoveryPending(true);
    const recovery = { action, turn, reloadAttempts: recoveryAttemptsRef.current + 1 };
    try {
      sessionStorage.setItem(voiceRecoveryStorageKey(), JSON.stringify(recovery));
      window.location.reload();
    } catch {
      try {
        window.history.replaceState({ ...window.history.state, demoVoiceRecovery: recovery }, "", window.location.href);
        window.location.reload();
      } catch {
        reloadingRef.current = false;
        recoveredActionRef.current = undefined;
        recoveryAttemptsRef.current = 0;
        setRecoveryPending(false);
        setError("The connection could not be refreshed automatically. Please refresh the page and try again.");
      }
    }
  }

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  useEffect(() => {
    resultsStateRef.current = resultsState;
  }, [resultsState]);

  useEffect(() => {
    providerRef.current = provider;
    storeVoiceProvider(provider);
  }, [provider]);

  useEffect(() => {
    const chatId = currentChatId();
    const url = chatId ? `${voiceDemoBasePath()}/state?chat_id=${encodeURIComponent(chatId)}` : `${voiceDemoBasePath()}/state`;
    jsonRequest(url, {}, demoRelease)
      .then((state: VoiceState) => {
        const restoredMessages = (state.messages || []).map((message, index) => ({
          ...message,
          sequence: Number(message.sequence || index + 1),
        }));
        sequenceRef.current = restoredMessages.reduce((max, message) => Math.max(max, message.sequence), 0);
        restoredMessages.forEach((message) => itemSequencesRef.current.set(message.id, message.sequence));
        setMessages(restoredMessages);
        const restoredResults = buildResultsState(state.widgets || []);
        resultsStateRef.current = restoredResults;
        setResultsState(restoredResults);
        if (debugEnabled) setToolCalls(state.tool_calls || []);
        if (debugEnabled && state.voice_usage) setVoiceUsage(state.voice_usage);
        if (restoredMessages.length && state.model_selection?.provider) {
          const restoredProvider = state.model_selection.provider === "gemini" ? "gemini" : "openai";
          providerRef.current = restoredProvider;
          setProvider(restoredProvider);
        }
        setCanWrite(state.can_write === true);
        setStateReady(true);
        if (state.can_write === false) setCaption("This shared conversation is read-only");
        if (!recoveredActionRef.current) recoveryAttemptsRef.current = 0;
      })
      .catch((stateError) => {
        if (stateError instanceof StaleVoiceClientError) {
          reloadForStaleClient(recoveredActionRef.current, recoveredAction?.turn);
          return;
        }
        setError("Could not restore this voice conversation.");
      })
      .finally(() => setStateLoaded(true));
  }, []);

  useEffect(() => {
    if (!stateReady || !recoveredActionRef.current) return;

    const action = recoveredActionRef.current;
    recoveredActionRef.current = undefined;
    if (action === "reset") void resetConversation(true);
    if (action === "start") {
      if (!canWrite) {
        void resetConversation(true);
        return;
      }
      void (async () => {
        const turn = recoveredAction?.turn;
        if (turn) {
          const restoredMessages = upsertMessage(messagesRef.current, turn);
          messagesRef.current = restoredMessages;
          setMessages(restoredMessages);
          try {
            await jsonRequest(`${voiceDemoBasePath()}/turns`, {
              method: "POST",
              body: JSON.stringify({ ...turn, chat_id: currentChatId() }),
            }, demoRelease);
          } catch (turnError) {
            if (turnError instanceof StaleVoiceClientError) reloadForStaleClient("start", turn);
            else {
              setRecoveryPending(false);
              setError("The transcript could not be saved.");
            }
            return;
          }
        }
        await startConversation(true);
      })();
    }
  }, [canWrite, stateReady]);

  useEffect(() => () => disconnect(false), []);

  function sendEvent(event: any) {
    if (channelRef.current?.readyState === "open") channelRef.current.send(JSON.stringify(event));
  }

  function sequenceFor(itemId: string) {
    const existing = itemSequencesRef.current.get(itemId);
    if (existing) return existing;
    const sequence = ++sequenceRef.current;
    itemSequencesRef.current.set(itemId, sequence);
    return sequence;
  }

  function persistTurn(message: VoiceMessage) {
    const nextMessages = upsertMessage(messagesRef.current, message);
    messagesRef.current = nextMessages;
    setMessages(nextMessages);
    void jsonRequest(`${voiceDemoBasePath()}/turns`, {
      method: "POST",
      body: JSON.stringify({ ...message, chat_id: currentChatId() }),
    }, demoRelease).catch((turnError) => {
      if (turnError instanceof StaleVoiceClientError) {
        reloadForStaleClient("start", message);
        return;
      }
      setError("The transcript could not be saved.");
    });
  }

  function currentUserQuery() {
    const liveTranscript = geminiUserDraftRef.current.trim();
    if (liveTranscript) return liveTranscript;

    return [...messagesRef.current]
      .reverse()
      .find((message) => message.role === "user" && message.content !== "...")
      ?.content;
  }

  function persistUsage(identity: string, kind: "realtime" | "transcription" | "gemini_live", usage: any) {
    if (!identity || !usage || processedUsageRef.current.has(`${kind}:${identity}`)) return;
    const key = `${kind}:${identity}`;
    processedUsageRef.current.add(key);
    void jsonRequest(`${voiceDemoBasePath()}/usage`, {
      method: "POST",
      body: JSON.stringify({ chat_id: currentChatId(), identity, kind, usage }),
    }, demoRelease).then((data) => {
      if (debugEnabled && data.usage) setVoiceUsage(data.usage);
    }).catch(() => {
      processedUsageRef.current.delete(key);
    });
  }

  function restoreConversation() {
    messagesRef.current.forEach((message) => {
      sendEvent({
        type: "conversation.item.create",
        item: {
          type: "message",
          role: message.role,
          content: [{ type: message.role === "user" ? "input_text" : "output_text", text: message.content }],
        },
      });
    });
  }

  async function executeTool(item: any) {
    if (!item?.call_id || processedCallsRef.current.has(item.call_id)) return;
    processedCallsRef.current.add(item.call_id);
    const connectionGeneration = connectionGenerationRef.current;
    const abortController = item.gemini_call ? new AbortController() : null;
    if (abortController) geminiToolAbortRef.current.set(item.call_id, abortController);
    setPhase("searching");
    setCaptionRole(null);

    let output: any;
    let args: any = {};
    try {
      try {
        args = typeof item.arguments === "string" ? JSON.parse(item.arguments || "{}") : (item.arguments || {});
      } catch {
        throw new Error("The voice agent returned invalid search details.");
      }
      const searchId = String(args.search_id || "");
      const hasResultsForSearch = resultsStateRef.current.sets.some((resultSet) => resultSet.searchIds.includes(searchId));
      const isInitialResultsCall = item.name === "get_next_best_itinerary" &&
        (String(args.current_result_id || "") === "0" || !hasResultsForSearch);
      setCaption(item.name === "start_itinerary_search"
        ? "Hold on a sec while I get your flight options..."
        : isInitialResultsCall
          ? "Give me a minute while I look through everything..."
          : "Finding your next best option...");
      const data = await jsonRequest(`${voiceDemoBasePath()}/tools`, {
        method: "POST",
        signal: abortController?.signal,
        body: JSON.stringify({
          chat_id: currentChatId(),
          name: item.name,
          arguments: args,
          user_query: item.name === "get_next_best_itinerary" ? currentUserQuery() : undefined,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          traveller_profile: readTravellerProfile(),
        }),
      }, demoRelease);
      if (cancelledGeminiCallsRef.current.has(item.call_id)) return;
      output = data.response;
      if (connectionGeneration !== connectionGenerationRef.current) return;
      if (debugEnabled) {
        setToolCalls((current) => [...current, data.debug || {
          name: data.name || item.name, arguments: args, response: data.response,
        }].slice(-20));
      }
      if (data.widget) {
        setResultsState((current) => {
          const next = integrateWidget(current, data.widget);
          resultsStateRef.current = next;
          return next;
        });
      }
    } catch (toolError) {
      geminiToolAbortRef.current.delete(item.call_id);
      if (abortController?.signal.aborted || cancelledGeminiCallsRef.current.has(item.call_id)) return;
      if (toolError instanceof StaleVoiceClientError) {
        reloadForStaleClient("start");
        return;
      }
      output = { error: toolError instanceof Error ? toolError.message : "The flight search failed." };
      if (connectionGeneration === connectionGenerationRef.current) {
        setError(output.error);
        if (debugEnabled) {
          setToolCalls((current) => [...current, {
            name: item.name, arguments: args, response: output,
          }].slice(-20));
        }
      }
    }

    if (connectionGeneration !== connectionGenerationRef.current) return;
    if (item.gemini_call && cancelledGeminiCallsRef.current.has(item.call_id)) return;
    geminiToolAbortRef.current.delete(item.call_id);

    if (providerRef.current === "gemini" && item.gemini_call) {
      geminiRef.current?.sendToolResponse(item.gemini_call, output);
    } else {
      sendEvent({
        type: "conversation.item.create",
        item: { type: "function_call_output", call_id: item.call_id, output: JSON.stringify(output) },
      });
      sendEvent({ type: "response.create" });
    }
    setPhase("thinking");
  }

  function finalizeGeminiUserTurn() {
    const userText = geminiUserDraftRef.current.trim();
    if (userText && geminiUserTurnIdRef.current) {
      const id = geminiUserTurnIdRef.current;
      persistTurn({ id, role: "user", content: userText, sequence: sequenceFor(id) });
    }
    geminiUserDraftRef.current = "";
    geminiUserTurnIdRef.current = null;
  }

  function finalizeGeminiAssistantTurn() {
    const assistantText = assistantDraftRef.current.trim();
    if (assistantText && geminiAssistantTurnIdRef.current) {
      const id = geminiAssistantTurnIdRef.current;
      persistTurn({ id, role: "assistant", content: assistantText, sequence: sequenceFor(id) });
    }
    assistantDraftRef.current = "";
    geminiAssistantTurnIdRef.current = null;
  }

  function finalizeGeminiTurns() {
    finalizeGeminiUserTurn();
    finalizeGeminiAssistantTurn();
  }

  function scheduleGeminiFinalization() {
    if (geminiFinalizeTimerRef.current) window.clearTimeout(geminiFinalizeTimerRef.current);
    geminiFinalizeTimerRef.current = window.setTimeout(() => {
      geminiFinalizeTimerRef.current = null;
      finalizeGeminiTurns();
    }, 500);
  }

  function reschedulePendingGeminiFinalization() {
    if (!geminiFinalizeTimerRef.current) return;
    window.clearTimeout(geminiFinalizeTimerRef.current);
    scheduleGeminiFinalization();
  }

  function handleGeminiUsage(usage: any) {
    if (!usage?.totalTokenCount && !usage?.promptTokenCount && !usage?.responseTokenCount && !usage?.thoughtsTokenCount) return;
    const identity = `gemini-${++geminiUsageSequenceRef.current}-${Number(usage.totalTokenCount || 0)}`;
    persistUsage(identity, "gemini_live", usage);
  }

  function handleRealtimeEvent(event: any) {
    switch (event.type) {
      case "input_audio_buffer.speech_started":
        setPhase("listening");
        setCaption("Listening...");
        setCaptionRole("user");
        break;
      case "input_audio_buffer.speech_stopped":
        setPhase("thinking");
        setCaption("Thinking...");
        setCaptionRole(null);
        break;
      case "input_audio_buffer.committed":
        if (event.item_id) {
          setMessages((current) => upsertMessage(current, {
            id: event.item_id,
            role: "user",
            content: "...",
            sequence: sequenceFor(event.item_id),
          }));
        }
        break;
      case "conversation.item.input_audio_transcription.completed": {
        const text = String(event.transcript || "").trim();
        if (event.usage) persistUsage(`${event.item_id}:${event.content_index || 0}`, "transcription", event.usage);
        if (text) {
          persistTurn({ id: event.item_id, role: "user", content: text, sequence: sequenceFor(event.item_id) });
          setCaption(text);
          setCaptionRole("user");
        }
        break;
      }
      case "response.output_audio_transcript.delta":
        assistantDraftRef.current += event.delta || "";
        setPhase("speaking");
        setCaption(assistantDraftRef.current);
        setCaptionRole("assistant");
        break;
      case "response.output_audio_transcript.done": {
        const text = String(event.transcript || assistantDraftRef.current).trim();
        if (text) persistTurn({
          id: event.item_id,
          role: "assistant",
          content: text,
          sequence: sequenceFor(event.item_id),
        });
        assistantDraftRef.current = "";
        break;
      }
      case "response.output_item.done":
        if (event.item?.type === "function_call") void executeTool(event.item);
        break;
      case "response.created":
        setPhase("thinking");
        break;
      case "response.done":
        if (event.response?.id && event.response?.usage) persistUsage(event.response.id, "realtime", event.response.usage);
        if (event.response?.status === "failed") {
          setError("Reagent could not complete that response.");
          setPhase("error");
        } else if (["cancelled", "incomplete"].includes(event.response?.status)) {
          setPhase("listening");
        } else if (event.response?.status === "completed" &&
          !Array.isArray(event.response?.output)) {
          setPhase("listening");
        } else if (event.response?.status === "completed" &&
          !event.response.output.some((item: any) => item.type === "function_call")) {
          setPhase("listening");
        }
        break;
      case "error":
        setError(event.error?.message || "The voice connection encountered an error.");
        setPhase("error");
        break;
    }
  }

  async function startGeminiConnection(sessionData: GeminiLiveSessionData, stream: MediaStream, connectionGeneration: number, audioContext: AudioContext) {
    geminiUsageSequenceRef.current = 0;
    cancelledGeminiCallsRef.current.clear();
    const connection = new GeminiLiveConnection({
      onReady: () => {
        if (connectionGeneration !== connectionGenerationRef.current) return;
        recoveryAttemptsRef.current = 0;
        setRecoveryPending(false);
        setPhase("listening");
        setCaption(messagesRef.current.length ? "I am listening. What should we refine?" : "I am listening. Tell me about your trip.");
        setCaptionRole(null);
      },
      onAudio: () => {
        setPhase("speaking");
        setCaptionRole("assistant");
      },
      onInputTranscript: (text, finished) => {
        reschedulePendingGeminiFinalization();
        if (!geminiUserTurnIdRef.current) geminiUserTurnIdRef.current = `gemini-user-${Date.now()}-${sequenceRef.current + 1}`;
        sequenceFor(geminiUserTurnIdRef.current);
        geminiUserDraftRef.current += text;
        setPhase(finished ? "thinking" : "listening");
        setCaption(geminiUserDraftRef.current);
        setCaptionRole("user");
        if (finished) finalizeGeminiUserTurn();
      },
      onOutputTranscript: (text, finished) => {
        reschedulePendingGeminiFinalization();
        if (!geminiUserTurnIdRef.current) {
          geminiUserTurnIdRef.current = `gemini-user-${Date.now()}-${sequenceRef.current + 1}`;
          sequenceFor(geminiUserTurnIdRef.current);
        }
        if (!geminiAssistantTurnIdRef.current) geminiAssistantTurnIdRef.current = `gemini-assistant-${Date.now()}-${sequenceRef.current + 1}`;
        sequenceFor(geminiAssistantTurnIdRef.current);
        assistantDraftRef.current += text;
        setPhase("speaking");
        setCaption(assistantDraftRef.current);
        setCaptionRole("assistant");
        if (finished) finalizeGeminiAssistantTurn();
      },
      onTurnComplete: () => {
        scheduleGeminiFinalization();
        setPhase("listening");
      },
      onInterrupted: () => {
        finalizeGeminiTurns();
        setPhase("listening");
        setCaption("I am listening");
        setCaptionRole(null);
      },
      onToolCall: (call) => {
        void executeTool({
          call_id: call.id,
          name: call.name,
          arguments: call.args || {},
          gemini_call: call,
        });
      },
      onToolCallCancellation: (ids) => ids.forEach((id) => {
        cancelledGeminiCallsRef.current.add(id);
        geminiToolAbortRef.current.get(id)?.abort();
        geminiToolAbortRef.current.delete(id);
      }),
      onUsage: handleGeminiUsage,
      onError: (message) => {
        setError(message);
        setPhase("error");
      },
      onClose: () => {
        if (geminiRef.current) {
          disconnect(false);
          setPhase("ended");
          setCaption("Conversation ended");
        }
      },
    });
    geminiRef.current = connection;
    await connection.connect(sessionData, stream, messagesRef.current, audioContext);
  }

  async function startConversation(recovering = false) {
    if (recoveryPending && !recovering) return;
    if (!navigator.mediaDevices?.getUserMedia ||
      (provider === "openai" && !window.RTCPeerConnection) ||
      (provider === "gemini" && !window.AudioContext)) {
      if (recovering) setRecoveryPending(false);
      setError("Voice conversations are not supported in this browser.");
      setPhase("error");
      return;
    }

    setError(null);
    setPhase("connecting");
    setCaption("Connecting to Reagent...");
    processedCallsRef.current.clear();
    const connectionGeneration = ++connectionGenerationRef.current;
    const geminiAudioContext = provider === "gemini" ? new AudioContext() : null;

    try {
      const sessionData = await jsonRequest(`${voiceDemoBasePath()}/session`, {
        method: "POST",
        body: JSON.stringify({
          chat_id: currentChatId(),
          provider,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          traveller_profile: readTravellerProfile(),
        }),
      }, demoRelease);
      if (connectionGeneration !== connectionGenerationRef.current) return;
      if (!sessionData.value) throw new Error("The voice session did not return a client secret.");

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      });
      if (connectionGeneration !== connectionGenerationRef.current) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }
      if (sessionData.provider === "gemini") {
        streamRef.current = stream;
        await retryOnce(() => startGeminiConnection(
          sessionData as GeminiLiveSessionData,
          stream,
          connectionGeneration,
          geminiAudioContext!,
        ));
        return;
      }
      const peer = new RTCPeerConnection();
      const audio = new Audio();
      audio.autoplay = true;
      peer.ontrack = (event) => {
        audio.srcObject = event.streams[0];
        void audio.play().catch(() => setError("Tap the page to enable voice playback."));
      };
      stream.getAudioTracks().forEach((track) => peer.addTrack(track, stream));

      const channel = peer.createDataChannel("oai-events");
      channel.addEventListener("message", (message) => {
        try {
          handleRealtimeEvent(JSON.parse(message.data));
        } catch {
          setError("The voice session sent an unreadable event.");
        }
      });
      channel.addEventListener("open", () => {
        restoreConversation();
        recoveryAttemptsRef.current = 0;
        setRecoveryPending(false);
        setPhase("listening");
        setCaption(messagesRef.current.length ? "I am listening. What should we refine?" : "I am listening. Tell me about your trip.");
        setCaptionRole(null);
      });
      channel.addEventListener("close", () => {
        if (peerRef.current) setPhase("ended");
      });

      peerRef.current = peer;
      channelRef.current = channel;
      streamRef.current = stream;
      audioRef.current = audio;

      const offer = await peer.createOffer();
      await peer.setLocalDescription(offer);
      if (connectionGeneration !== connectionGenerationRef.current) {
        peer.close();
        stream.getTracks().forEach((track) => track.stop());
        return;
      }
      const sdpResponse = await retryOnce(async () => {
        const response = await fetch("https://api.openai.com/v1/realtime/calls", {
          method: "POST",
          body: offer.sdp,
          headers: { Authorization: `Bearer ${sessionData.value}`, "Content-Type": "application/sdp" },
        });
        if (!response.ok) throw new Error("OpenAI could not establish the voice connection.");
        return response;
      });
      if (connectionGeneration !== connectionGenerationRef.current) {
        peer.close();
        stream.getTracks().forEach((track) => track.stop());
        return;
      }
      await peer.setRemoteDescription({ type: "answer", sdp: await sdpResponse.text() });
      if (connectionGeneration !== connectionGenerationRef.current) {
        peer.close();
        stream.getTracks().forEach((track) => track.stop());
      }
    } catch (startError) {
      disconnect(false);
      void geminiAudioContext?.close();
      if (startError instanceof StaleVoiceClientError) {
        reloadForStaleClient("start");
        return;
      }
      if (recovering) setRecoveryPending(false);
      setError(startError instanceof Error ? startError.message : "Could not start the voice conversation.");
      setPhase("error");
      setCaption("Could not connect");
    }
  }

  function toggleMute() {
    const nextMuted = !muted;
    streamRef.current?.getAudioTracks().forEach((track) => { track.enabled = !nextMuted; });
    setMuted(nextMuted);
    setCaption(nextMuted ? "Microphone muted" : "I am listening");
    setCaptionRole(null);
  }

  function disconnect(showEnded = true) {
    connectionGenerationRef.current += 1;
    if (geminiFinalizeTimerRef.current) window.clearTimeout(geminiFinalizeTimerRef.current);
    geminiFinalizeTimerRef.current = null;
    finalizeGeminiTurns();
    geminiToolAbortRef.current.forEach((controller) => controller.abort());
    geminiToolAbortRef.current.clear();
    geminiRef.current?.close();
    channelRef.current?.close();
    peerRef.current?.close();
    streamRef.current?.getTracks().forEach((track) => track.stop());
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.srcObject = null;
    }
    channelRef.current = null;
    geminiRef.current = null;
    peerRef.current = null;
    streamRef.current = null;
    audioRef.current = null;
    setMuted(false);
    if (showEnded) {
      setPhase("ended");
      setCaption("Conversation ended");
      setCaptionRole(null);
    }
  }

  async function resetConversation(recovering = false) {
    if (recoveryPending && !recovering) return;
    disconnect(false);
    try {
      const data = await jsonRequest(voiceDemoBasePath(), {
        method: "DELETE",
        body: JSON.stringify({ chat_id: currentChatId() }),
      }, demoRelease);
      recoveryAttemptsRef.current = 0;
      window.location.assign(data.redirect_url || voiceDemoBasePath());
    } catch (resetError) {
      if (resetError instanceof StaleVoiceClientError) {
        reloadForStaleClient("reset");
        return;
      }
      if (recovering) setRecoveryPending(false);
      setError("Could not reset the conversation.");
    }
  }

  const connected = ["listening", "thinking", "speaking", "searching"].includes(phase);
  const canStart = stateLoaded && canWrite && !recoveryPending && ["idle", "ended", "error"].includes(phase);
  const hasVisibleResults = !voiceOnly && resultsState.sets.length > 0;

  function closeTranscript() {
    transcriptButtonRef.current?.focus();
    setTranscriptOpen(false);
  }

  return (
    <main className="min-h-screen bg-[#f7f8fa] text-gray-950 dark:bg-black dark:text-gray-100">
      <header className="sticky top-0 z-20 border-b border-black/5 bg-[#f7f8fa]/85 px-3 py-3 backdrop-blur-xl dark:border-white/10 dark:bg-black/80 sm:px-4">
        <div className="mx-auto flex max-w-[1100px] items-center justify-between gap-4">
          <a href={voiceDemoBasePath()} aria-label="Start a new voice demo" className="flex items-center gap-3">
            <img src={appPath("/pwa-icon-180.png")} alt="Reagent" className="h-8 w-8 rounded-lg object-contain sm:hidden" />
            <img src={appPath("/images/logo_with_word_blue.png")} alt="" className="hidden h-8 w-auto object-contain sm:block" />
            <span className="hidden border-l border-gray-300 pl-3 text-sm font-medium text-gray-500 dark:border-white/20 dark:text-gray-400 sm:block">{voiceOnly ? "Voice-only flight search" : "Voice flight search"}</span>
          </a>
          <div className="flex items-center gap-0 sm:gap-1">
            <VoiceModelSelector provider={provider} disabled={messages.length > 0 || connected || phase === "connecting"} onSelect={setProvider} />
            <button ref={transcriptButtonRef} type="button" onClick={() => setTranscriptOpen(true)} className="rounded-full px-2 py-2 text-sm font-medium text-gray-600 transition hover:bg-black/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 dark:text-gray-300 dark:hover:bg-white/10 sm:px-3">Transcript</button>
            <ProfileMenu
              disabled={recoveryPending}
              menuButtonRef={profileButtonRef}
              onAdjustProfile={() => setProfileOpen(true)}
              onImportProfile={() => setProfileImportOpen(true)}
              onReset={() => resetConversation()}
            />
          </div>
        </div>
      </header>

      <section className={`voice-stage mx-auto flex w-full max-w-[1100px] flex-col items-center px-5 py-10 text-center sm:py-14 ${hasVisibleResults ? "min-h-[52vh] sm:min-h-[58vh]" : "min-h-[calc(100dvh-65px)]"}`}>
        <div className="my-auto flex w-full flex-col items-center">
        <h1 className="sr-only">{voiceOnly ? "Voice-only flight search" : "Voice flight search"}</h1>
        <p className="mb-7 text-xs font-semibold uppercase tracking-[0.18em] text-gray-400" aria-live="polite">{voiceOnly && muted && connected ? "Microphone muted" : PHASE_LABELS[phase]}</p>
        <VoiceOrb phase={phase} />
        {!voiceOnly ? (
          <div className="mt-9 min-h-20 max-w-2xl" aria-live={phase === "speaking" ? "off" : "polite"} aria-atomic="false">
            {captionRole ? <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-400">{captionRole === "user" ? "You" : "Reagent"}</div> : null}
            <p className="line-clamp-3 text-lg font-medium leading-7 tracking-tight text-gray-800 dark:text-gray-100 sm:text-xl">{caption}</p>
          </div>
        ) : null}

        <div className={`${voiceOnly ? "mt-10" : "mt-7"} flex items-center justify-center gap-3`}>
          {canStart ? (
            <button type="button" onClick={() => startConversation()} className="rounded-full bg-gray-950 px-6 py-3 text-sm font-semibold text-white shadow-lg transition hover:scale-[1.02] hover:bg-gray-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 dark:bg-white dark:text-black dark:hover:bg-gray-100">
              Start voice conversation
            </button>
          ) : null}
          {connected ? (
            <>
              <IconButton label={muted ? "Unmute microphone" : "Mute microphone"} active={!muted} onClick={toggleMute}>
                {muted ? (
                  voiceOnly ? (
                    <svg aria-hidden="true" viewBox="0 0 24 24" className="h-5 w-5 fill-none stroke-current stroke-2"><rect x="9" y="3" width="6" height="11" rx="3"/><path d="M5 11a7 7 0 0 0 11.8 5.1M12 18v3M9 21h6M4 4l16 16"/></svg>
                  ) : <span aria-hidden="true" className="text-sm font-bold">M</span>
                ) : (
                  <svg aria-hidden="true" viewBox="0 0 24 24" className="h-5 w-5 fill-none stroke-current stroke-2"><rect x="9" y="3" width="6" height="11" rx="3"/><path d="M5 11a7 7 0 0 0 14 0M12 18v3M9 21h6"/></svg>
                )}
              </IconButton>
              <IconButton label="End conversation" danger onClick={() => disconnect(true)}>
                <svg aria-hidden="true" viewBox="0 0 24 24" className="h-5 w-5 fill-current"><path d="M5.2 10.4c4.5-2.2 9.1-2.2 13.6 0 .8.4 1.2 1.3.9 2.1l-.8 2.1c-.3.8-1.2 1.2-2 .9l-2.2-.8c-.6-.2-1-.8-1-1.4v-.7a10 10 0 0 0-3.4 0v.7c0 .6-.4 1.2-1 1.4l-2.2.8c-.8.3-1.7-.1-2-.9l-.8-2.1c-.3-.8.1-1.7.9-2.1Z"/></svg>
              </IconButton>
            </>
          ) : null}
        </div>
        {phase === "idle" && !messages.length ? <p className="mt-5 max-w-md text-sm leading-6 text-gray-500 dark:text-gray-400">Try: "SFO to New York next Monday, back Wednesday evening."</p> : null}
        {stateLoaded && !canWrite ? <p className="mt-5 max-w-md text-sm leading-6 text-gray-500 dark:text-gray-400">Start a new conversation to continue searching by voice.</p> : null}
        {error ? <div role="alert" className="mt-5 max-w-lg rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-950 dark:text-red-200">{error}</div> : null}
        </div>
      </section>

      {hasVisibleResults ? (
        <FlightResults
          resultsState={resultsState}
          onSelect={(resultSetId) => setResultsState((current) => ({ ...current, selectedId: resultSetId }))}
        />
      ) : null}
      {debugEnabled ? <ToolCallsDebug toolCalls={toolCalls} usage={voiceUsage} /> : null}
      <TranscriptDrawer messages={messages} open={transcriptOpen} onClose={closeTranscript} />
      <ProfileDialog
        key={profileRevision}
        open={profileOpen}
        onClose={() => {
          setProfileOpen(false);
          window.requestAnimationFrame(() => profileButtonRef.current?.focus());
        }}
      />
      <ImportProfileDialog
        open={profileImportOpen}
        onImported={() => setProfileRevision((revision) => revision + 1)}
        onClose={() => {
          setProfileImportOpen(false);
          window.requestAnimationFrame(() => profileButtonRef.current?.focus());
        }}
      />

      <style>{`
        .voice-orb { position: relative; isolation: isolate; overflow: visible; width: clamp(10rem, 25vw, 16rem); aspect-ratio: 1; pointer-events: none; }
        .voice-orb__halo { position: absolute; inset: -22%; border-radius: 50%; background: radial-gradient(circle, rgba(38, 116, 255, .2) 0 36%, rgba(38, 116, 255, .1) 52%, transparent 72%); opacity: .5; transition: opacity .4s ease, transform .4s ease; }
        .voice-orb__body { position: absolute; inset: 0; overflow: hidden; border-radius: 50%; clip-path: circle(50% at 50% 50%); background: #1264df; box-shadow: inset -25px -30px 55px rgba(0, 20, 75, .24), inset 18px 20px 45px rgba(255,255,255,.28), 0 24px 70px rgba(20, 89, 207, .22); }
        .voice-orb__wash { position: absolute; width: 88%; height: 88%; border-radius: 45% 55% 58% 42%; filter: blur(18px); mix-blend-mode: screen; }
        .voice-orb__wash--one { left: -20%; top: -8%; background: #78e8f2; animation: voice-drift-one 8s ease-in-out infinite alternate; }
        .voice-orb__wash--two { right: -25%; bottom: -15%; background: #8c6dff; animation: voice-drift-two 10s ease-in-out infinite alternate; }
        .voice-orb__shine { position: absolute; left: 18%; top: 12%; width: 42%; height: 28%; border-radius: 50%; background: rgba(255,255,255,.42); filter: blur(22px); transform: rotate(-18deg); }
        .voice-orb--listening .voice-orb__halo { opacity: .9; animation: voice-pulse 1.8s ease-out infinite; }
        .voice-orb--speaking .voice-orb__body, .voice-orb--searching .voice-orb__body { animation: voice-breathe 1.15s ease-in-out infinite alternate; }
        .voice-orb--thinking .voice-orb__body, .voice-orb--connecting .voice-orb__body { animation: voice-turn 4s linear infinite; }
        .voice-orb--ended .voice-orb__body, .voice-orb--error .voice-orb__body { filter: saturate(.35); opacity: .74; }
        .voice-orb--ended .voice-orb__halo, .voice-orb--error .voice-orb__halo { opacity: .2; }
        .demo-voice-itinerary-widget :is(a, button)[data-pill] { border-radius: 9999px !important; }
        .demo-voice-itinerary-widget :is(a, button)[data-variant] {
          min-height: 2.75rem !important;
          display: inline-flex !important;
          align-items: center !important;
          justify-content: center !important;
          font-weight: 600 !important;
          line-height: 1.2 !important;
          text-decoration: none !important;
          -webkit-appearance: none !important;
          appearance: none !important;
        }
        .demo-voice-itinerary-widget :is(a, button)[data-variant] > span {
          color: inherit !important;
          -webkit-text-fill-color: inherit !important;
        }
        .demo-voice-itinerary-widget :is(a, button)[data-variant="solid"] {
          background: #f4f4f4 !important;
          color: #111 !important;
          -webkit-text-fill-color: #111 !important;
          border: 0 !important;
          padding-left: 1.35rem !important;
          padding-right: 1.35rem !important;
        }
        .demo-voice-itinerary-widget :is(a, button)[data-variant="soft"][data-color="secondary"] {
          background: #3b3b3b !important;
          color: #fff !important;
          -webkit-text-fill-color: #fff !important;
          border: 0 !important;
        }
        .demo-voice-itinerary-widget :is(a, button):focus-visible {
          outline: 2px solid rgba(255, 255, 255, .45) !important;
          outline-offset: 2px !important;
        }
        .demo-voice-itinerary-widget[data-demo-theme="light"] :is(a, button) {
          --button-background-color: #f4f4f4 !important;
          --button-background-color-hover: #e8e8e8 !important;
          --button-background-color-active: #ddd !important;
          --button-text-color: #111 !important;
          --button-ring-color: rgba(17, 17, 17, .28) !important;
          background: #f4f4f4 !important;
          color: #111 !important;
          border-color: transparent !important;
          -webkit-text-fill-color: #111 !important;
        }
        .demo-voice-itinerary-widget[data-demo-theme="light"] :is(a, button)::before {
          background-color: var(--button-background-color) !important;
          box-shadow: none !important;
        }
        .demo-voice-itinerary-widget[data-demo-theme="light"] :is(a, button)[data-variant="soft"][data-color="secondary"] {
          --button-background-color: #ececec !important;
          --button-background-color-hover: #e0e0e0 !important;
          --button-background-color-active: #d6d6d6 !important;
          --button-text-color: #111 !important;
          background: #ececec !important;
          color: #111 !important;
          -webkit-text-fill-color: #111 !important;
        }
        @media (max-width: 767px), (hover: none) and (pointer: coarse) {
          .demo-voice-itinerary-widget :is(a, button)[data-variant="soft"][data-color="secondary"] {
            min-height: 2.25rem !important;
            height: 2.25rem !important;
            padding: 0 1.5rem !important;
            min-width: 7rem !important;
          }
          .demo-voice-itinerary-widget :is(a, button)[data-variant="solid"] {
            min-height: 2.25rem !important;
            height: 2.25rem !important;
            padding-top: 0 !important;
            padding-bottom: 0 !important;
          }
          .demo-voice-itinerary-widget[data-demo-theme="light"] :is(a, button)[data-variant="solid"] {
            --button-background-color: #2f2f2f !important;
            --button-background-color-hover: #242424 !important;
            --button-background-color-active: #1a1a1a !important;
            --button-text-color: #fff !important;
            background: #2f2f2f !important;
            color: #fff !important;
            -webkit-text-fill-color: #fff !important;
          }
          .demo-voice-itinerary-widget[data-demo-theme="light"] :is(a, button)[data-variant="solid"]::before {
            background-color: #2f2f2f !important;
          }
        }
        @keyframes voice-drift-one { to { transform: translate(48%, 32%) rotate(55deg) scale(1.1); } }
        @keyframes voice-drift-two { to { transform: translate(-42%, -38%) rotate(-48deg) scale(.9); } }
        @keyframes voice-pulse { 0% { transform: scale(.9); opacity: .75; } 75%,100% { transform: scale(1.28); opacity: 0; } }
        @keyframes voice-breathe { to { transform: scale(1.045); } }
        @keyframes voice-turn { to { transform: rotate(360deg); } }
        @media (prefers-reduced-motion: reduce) {
          .voice-orb *, .voice-orb { animation: none !important; }
        }
      `}</style>
    </main>
  );
}

const root = document.getElementById("demo-voice-root");
if (root) {
  createRoot(root).render(
    <VoiceDemoApp
      debugEnabled={root.dataset.demoProduction !== "true"}
      demoRelease={root.dataset.demoRelease}
      recoveredAction={takeVoiceRecovery()}
      voiceOnly={root.dataset.demoVoiceOnly === "true"}
    />
  );
}
